import type { InjectionKey, Ref, ComputedRef } from 'vue';
import { injectStrict } from '../../utils';
import type { GenericObject } from '../../../shared/types';
import type { UiConfig } from '../../shared/ui-config';
import type { UiDataTableSorting, UiDataTableColumn } from './interfaces';
interface CellContext {
  align: 'start' | 'end' | 'center';
  monospace: boolean;
  row?: GenericObject;
  columnKey?: string;
  filterKey?: string;
  filterValueGetter?: (row: GenericObject) => unknown;
  value: string;
  rawValue: any;
}
export const CELL_CONTEXT: InjectionKey<CellContext> = Symbol(__DEV__ ? 'CellContext' : '');
export interface TableContext<T extends GenericObject = GenericObject> {
  pinnedColumnsStartKeys: Ref<string[]>;
  pinnedColumnsEndKeys: Ref<string[]>;
  sorting: Ref<UiDataTableSorting>;
  setSorting: (value: UiDataTableSorting) => void;
  hideColumn: (key: string) => void;
  startDragging: (event: MouseEvent, key: string) => void;
  draggedColumnIndex: ComputedRef<number>;
  targetColumnIndex: ComputedRef<number>;
  draggedColumnKey: Ref<string | null>;
  targetColumnKey: Ref<string | null>;
  visibleColumns: ComputedRef<UiDataTableColumn<T>[]>;
  regularColumns: ComputedRef<UiDataTableColumn<T>[]>;
  pinnedColumnsStart: ComputedRef<UiDataTableColumn<T>[]>;
  pinnedColumnsEnd: ComputedRef<UiDataTableColumn<T>[]>;
  columnsResizing: ComputedRef<boolean>;
  columnsVisibilityControl: ComputedRef<boolean>;
  columnsPinControl: ComputedRef<boolean>;
  columnsReordering: ComputedRef<boolean>;
  filterByColumnEnabled: ComputedRef<boolean>;
  filterByValueEnabled: ComputedRef<boolean>;
  askAiByColumnEnabled: ComputedRef<boolean>;
  askAiByValueEnabled: ComputedRef<boolean>;
  filterableColumnKeys: ComputedRef<Set<string>>;
  filterByColumn: (columnKey: string, filterKey?: string) => void;
  filterByValue: (columnKey: string, filterKey: string | undefined, value: unknown) => void;
  askAiByColumn: (column: UiDataTableColumn<T>) => void;
  askAiByValue: (payload: {
    row: T;
    columnKey: string;
    filterKey?: string;
    value: unknown;
    rawValue: unknown;
    displayValue: string;
  }) => void;
  activeContextMenuKey: Ref<string | null>;
  setActiveContextMenuKey: (value: string | null) => void;
  getRowKey: (row: T) => string | number;
  pinColumn: (key: string) => void;
  unpinColumn: (key: string) => void;
  resolveI18n: <K extends keyof UiConfig['i18n']['dataTable']>(
    key: K,
    ...args: UiConfig['i18n']['dataTable'][K] extends (...args: infer P) => string ? P : []
  ) => string;
}
export const TABLE_CONTEXT = Symbol(__DEV__ ? 'TableContext' : '') as InjectionKey<
  TableContext<any>
>;
export function useTableContext<T extends GenericObject = GenericObject>() {
  return injectStrict<TableContext<T>>(TABLE_CONTEXT);
}
